import { tagSlots } from "@stubhub/broadway-foundation";
import { css, sva } from "@stubhub/broadway-foundation/css";
import { eventGridListItemTagsSlots } from "@stubhub/ensemble-discovery";

export const mlbDateCardHeadingPlayoff = css({
  textAlign: "end",
});

/**
 * Container for the portion stack inside a multi-event day card. Single-use,
 * no variants — kept as a plain `css` class.
 */
export const mlbDateCardPortionContainer = css({
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  columnGap: "primitives.50",
  rowGap: "primitives.50",
  paddingBlockStart: "primitives.0",
  paddingBlockEnd: "primitives.50",
  paddingInlineStart: "primitives.50",
  paddingInlineEnd: "primitives.50",
  height: "full",
});

/**
 * Slot recipe for the full-size MLB single-event card body.
 *
 * Variants:
 * - `ticketAvailability` — `"soldOut"` dims the heading/subtitle text and
 *   de-saturates the opponent logo so the tile reads as sold-out;
 *   `"available"` renders normally.
 */
export const mlbDateCardFull = sva({
  slots: ["body", "logo", "headingLine", "subtitleLine", "parkingTag"],
  className: "mlbDateCardFull",
  base: {
    body: {
      display: "flex",
      flexDirection: "column",
      flexBasis: "auto",
      flexGrow: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingBlock: "primitives.200",
      paddingInline: "primitives.100",
    },
    logo: { height: "[40px]" },
    headingLine: {},
    subtitleLine: { color: "text.subtle" },
    parkingTag: { marginBlockStart: "primitives.50" },
  },
  variants: {
    ticketAvailability: {
      soldOut: {
        logo: {
          filter: "[grayscale(1)]",
          opacity: 0.8,
        },
        headingLine: { color: "text.disabled" },
        subtitleLine: { color: "text.disabled" },
      },
      available: {},
    },
  },
  defaultVariants: {
    ticketAvailability: "available",
  },
});

/**
 * Slot recipe for the condensed MLB portion (used inside multi-event days).
 *
 * Variants:
 * - `ticketAvailability` — `"soldOut"` tightens the text-block max width to
 *   leave room for the bell icon, dims every text line and de-saturates the
 *   opponent logo; `"available"` renders normally.
 */
export const mlbDateCardPortion = sva({
  slots: ["body", "text", "textLine", "subtleLine", "notificationIcon", "logo"],
  className: "mlbDateCardPortion",
  base: {
    body: {
      width: "full",
      height: "full",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexGrow: 1,
      columnGap: "primitives.50",
      rowGap: "primitives.50",
      paddingInline: "primitives.100",
    },
    text: {
      flexGrow: 1,
      flexBasis: "auto",
      // Full width − logo width (32px) − gap.
      maxWidth: "[calc(100% - 32px - {spacing.primitives.50})]",
      whiteSpace: "nowrap",
    },
    textLine: {
      overflowX: "hidden",
      overflowY: "hidden",
      textOverflow: "ellipsis",
    },
    subtleLine: {
      overflowX: "hidden",
      overflowY: "hidden",
      textOverflow: "ellipsis",
      color: "text.subtle",
    },
    notificationIcon: { flexShrink: 0 },
    logo: {
      width: "primitives.400",
      flexShrink: 0,
    },
  },
  variants: {
    ticketAvailability: {
      soldOut: {
        text: {
          // Full width − logo (32px) − icon (16px) − two gaps.
          maxWidth: "[calc(100% - 32px - 16px - {spacing.primitives.50} - {spacing.primitives.50})]",
          color: "text.disabled",
        },
        textLine: { color: "text.disabled" },
        subtleLine: { color: "text.disabled" },
        logo: {
          filter: "[grayscale(1)]",
          opacity: 0.8,
        },
      },
      available: {},
    },
  },
  defaultVariants: {
    ticketAvailability: "available",
  },
});

export const calendarTagsRecipe = sva({
  className: "calendarTags",
  slots: eventGridListItemTagsSlots,
  base: {
    container: {
      justifyContent: "center",
      maxWidth: "full",
      textAlign: "center",
    },
  },
  variants: {
    spacing: {
      clean: {},
      padded: {
        container: {
          paddingBlock: "primitives.50",
          paddingInline: "primitives.50",
        },
      },
    },
  },
  defaultVariants: {
    spacing: "clean",
  },
});

export const calendarTagRecipe = sva({
  slots: tagSlots,
  className: "calendarTag",
  base: {
    root: { maxWidth: "full" },
    icon: { alignSelf: "center" },
    label: {
      overflowX: "hidden",
      textOverflow: "ellipsis",
    },
  },
});
